import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enrollmentService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { getApiErrorMessage } from '../utils/apiErrors';
import { useConfirm } from '../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Select, Field } from './ui';

const EMPTY_FORM = { code: '', discount_type: 'percentage', discount_value: '', max_uses: '', valid_from: '', valid_until: '', is_active: true };

function ModuleCoupons({ moduleId }) {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchCoupons = () => {
    setLoading(true);
    enrollmentService.getCoupons({ module: moduleId })
      .then((res) => {
        const data = res.data;
        setCoupons(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => addNotification(t('Failed to load coupons'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchCoupons, [moduleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...c,
      valid_from: c.valid_from ? c.valid_from.slice(0, 10) : '',
      valid_until: c.valid_until ? c.valid_until.slice(0, 10) : '',
      max_uses: c.max_uses ?? '',
    });
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        module: moduleId,
        max_uses: form.max_uses === '' ? null : form.max_uses,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
      };
      if (editing) {
        const res = await enrollmentService.updateCoupon(editing.id, payload);
        setCoupons((prev) => prev.map((c) => (c.id === editing.id ? res.data : c)));
      } else {
        const res = await enrollmentService.createCoupon(payload);
        setCoupons((prev) => [...prev, res.data]);
      }
      addNotification(t('Coupon saved'), 'success');
      setModalOpen(false);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save coupon'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!(await confirm(t('Delete coupon "{{code}}"?', { code: c.code })))) return;
    try {
      await enrollmentService.deleteCoupon(c.id);
      setCoupons((prev) => prev.filter((x) => x.id !== c.id));
      addNotification(t('Coupon deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete coupon'), 'error');
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{t('Discount Coupons')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('Codes students can apply at enrollment time for a discount on this course.')}</p>
        </div>
        <Button size="sm" onClick={openCreate}>+ {t('Add Coupon')}</Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('Loading...')}</p>
      ) : coupons.length === 0 ? (
        <p className="text-sm text-gray-500">{t('No coupons yet.')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {coupons.map((c) => (
            <div key={c.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-start justify-between">
                <p className="font-mono font-semibold text-gray-900 text-sm">{c.code}</p>
                <Badge tone={c.is_active ? 'green' : 'gray'}>{c.is_active ? t('Active') : t('Inactive')}</Badge>
              </div>
              <p className="text-sm text-gray-700 mt-1">
                {c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`} {t('off')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('Used')}: {c.times_used}{c.max_uses ? ` / ${c.max_uses}` : ` (${t('unlimited')})`}
                {c.max_uses != null && c.times_used >= c.max_uses && (
                  <span className="text-red-600 font-medium"> · {t('Limit reached')}</span>
                )}
              </p>
              {(c.valid_from || c.valid_until) && (
                <p className="text-xs text-gray-500">
                  {c.valid_from && `${t('from')} ${new Date(c.valid_from).toLocaleDateString()}`}
                  {c.valid_from && c.valid_until && ' — '}
                  {c.valid_until && `${t('expires')} ${new Date(c.valid_until).toLocaleDateString()}`}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="secondary" onClick={() => openEdit(c)}>{t('Edit')}</Button>
                <Button size="sm" variant="danger" onClick={() => remove(c)}>{t('Delete')}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('Edit Coupon') : t('New Coupon')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Code')} required hint={t('Case-insensitive, will be stored uppercase')}>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="WELCOME10" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('Discount Type')} required>
              <Select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
                <option value="percentage">{t('Percentage')}</option>
                <option value="fixed">{t('Fixed Amount')}</option>
              </Select>
            </Field>
            <Field label={t('Discount Value')} required>
              <Input type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} />
            </Field>
          </div>
          <Field label={t('Max Uses')} hint={t('Leave blank for unlimited — e.g. set 10 and only the first 10 students to enroll can use this code')}>
            <Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('Valid From')} hint={t('Leave blank to start immediately')}>
              <Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
            </Field>
            <Field label={t('Valid Until')} hint={t('Leave blank for no expiry')}>
              <Input type="date" value={form.valid_until} min={form.valid_from || undefined} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('Active')}
          </label>
        </div>
      </Modal>
    </Card>
  );
}

export default ModuleCoupons;
